import { format as formatDateFns, formatISO, isValid, isWeekend, parse, parseISO } from 'date-fns';
import { addDays, endOfDay } from 'date-fns/fp';
import {
	__,
	compose,
	curry,
	flatMap,
	includes,
	isDate,
	map,
	over,
	overEvery,
	overSome,
} from 'lodash/fp';

/**
 * Converts string representation of date to other format. Utilizes date-fns library, which uses
 * standardised unicode tokens (NOTE: these tokens differs from moment.js)
 * @see https://date-fns.org/v2.15.0/docs/format
 * @see https://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table
 * @param {string} formatFrom
 * @param {string} formatTo
 * @param {Date} [referenceDate=new Date()] will be used to fill insufficient data in input (e.g.
 * 	'January 1st' without a year or gather century for double digit year)
 * @param {object} [options] date-fns parsing and formatting options
 * @see https://date-fns.org/v2.15.0/docs/parse#arguments
 * @return {function(stringDate: string): string}
 */
export const convertStringDate = (
	formatFrom,
	formatTo,
	referenceDate = new Date(),
	{ ...other } = {}
) => {
	const options = { ...other };
	return (date) =>
		date && formatDateFns(parse(date, formatFrom, referenceDate, options), formatTo, options);
};

export const formatingDate = (date, format) => formatDateFns(date, format);

export const formatDate = curry((format, date) => formatingDate(date, format));

/**
 * Checks if input is instance of Date and weather it contains valid date.
 * NOTE: date-fns/isValid function accepts also numbers as valid input.
 * @param {any} date
 * @return {boolean}
 */
export const isValidDate = overEvery([isDate, isValid]);

/**
 * Converts Date into ISO8601 date string
 * @param {Date} date
 * @return {string}
 */
export const toIsoDateString = (date) =>
	isValidDate(date) ? formatISO(date, { representation: 'date' }) : '';

const czechSaintsDays = [
	'01-01',
	'05-01',
	'05-08',
	'07-05',
	'07-06',
	'09-28',
	'10-28',
	'11-17',
	'12-24',
	'12-25',
	'12-26',
];

const czechMonthsOrdinal = [
	'lednového',
	'únorového',
	'březnového',
	'dubnového',
	'květnového',
	'červnového',
	'červencového',
	'srpnového',
	'zářijového',
	'říjnového',
	'listopadového',
	'prosincového',
];

const easternSundays = [
	'2021-04-04',
	'2022-04-17',
	'2023-04-09',
	'2024-03-31',
	'2025-04-20',
	'2026-04-05',
	'2027-28-03',
	'2028-04-16',
	'2029-04-01',
];
const easternFridaysAndMondays = compose(
	map(toIsoDateString),
	flatMap(compose(over([addDays(-2), addDays(1)]), parseISO))
)(easternSundays);

export const isCzechSaintsDay = compose(
	overSome([
		compose(includes(__, czechSaintsDays), (isoDate) => isoDate.slice(-5)),
		includes(__, easternFridaysAndMondays),
	]),
	toIsoDateString
);

export const isWorkday = (date) => !isWeekend(date) && !isCzechSaintsDay(date);

export const nextWorkday = (date) => {
	let workday = date;
	// eslint-disable-next-line no-cond-assign
	while (!isWorkday((workday = addDays(1, workday))));
	return workday;
};

/**
 * @param {string} dueDate an ISO8601 date
 * @return {Date} due date end of the day
 */
export const parseDueDate = compose(endOfDay, Date.parse);

export function isDateLike(dateLike) {
	if (typeof dateLike !== 'string') {
		return false;
	}

	return dateLike.split('-').length === 3;
}

export function getCzechMonthOrdinal(month) {
	return czechMonthsOrdinal[month];
}
